import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { DownloadIcon } from '@heroicons/react/outline';
import { useSearchParams } from 'react-router-dom';
import { GET_RETURN_ANALYTICS_REPORT } from 'gql/queries';
import {
  ReturnAnalyticsInput,
  ReturnAnalyticsReport,
} from 'types/returns-report';
import { getMerchantRequestsContext } from 'utils/merchantRequestsTransport';
import {
  buildReturnReportCsv,
  buildReturnReportTable,
  formatMoneyTotals,
  formatReportRate,
  ReturnReportBreakdown,
} from './return-report-table';

interface ReturnsAnalyticsPanelProps {
  storeId: string;
  startDate: string;
  endDate: string;
  onLoadingChange?: (loading: boolean) => void;
}

interface ReturnAnalyticsQueryResult {
  returnAnalytics?: ReturnAnalyticsReport | null;
}

type FilterValues = Pick<
  ReturnAnalyticsInput,
  'sku' | 'productLine' | 'reason' | 'resolution'
>;

const PAGE_SIZE = 25;
const breakdowns: Array<{ value: ReturnReportBreakdown; label: string }> = [
  { value: 'SKU_REASON', label: 'SKU × reason' },
  { value: 'PRODUCT_LINE', label: 'Product line' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'REQUESTED_OUTCOME', label: 'Requested outcome' },
  { value: 'RETURN_ITEMS', label: 'Request items' },
];
const validBreakdowns = new Set(breakdowns.map(({ value }) => value));
const emptyFilters: FilterValues = {
  sku: null,
  productLine: null,
  reason: null,
  resolution: null,
};
const fieldClass =
  'mt-1 block h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500';
const numericColumns = new Set([
  'Requests',
  'Request items',
  'Orders with requests',
  'Requested quantity',
  'Fulfilled orders',
  'Request activity ratio',
  'Previous month ratio',
  'Change',
  'Quantity',
  'Original item value',
  'Label cost',
]);

const trimmedParam = (params: URLSearchParams, key: string) =>
  params.get(key)?.trim() || null;

const readFilters = (params: URLSearchParams): FilterValues => ({
  sku: trimmedParam(params, 'rrSku'),
  productLine: trimmedParam(params, 'rrLine'),
  reason: trimmedParam(params, 'rrReason'),
  resolution: trimmedParam(params, 'rrResolution'),
});

const displayLabel = (value: string) => {
  const normalized = value.trim().replace(/[_-]+/g, ' ').toLowerCase();
  return normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : value;
};

const finiteNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value);
const optionalNumber = (value: unknown) =>
  value === null || finiteNumber(value);
const nullableText = (value: unknown) =>
  value === null || typeof value === 'string';
const moneyTotals = (value: unknown) =>
  Array.isArray(value) &&
  value.every(
    (total) =>
      total && typeof total.currency === 'string' && finiteNumber(total.amount),
  );
const reportMetrics = (value: any) =>
  value &&
  finiteNumber(value.returnCount) &&
  finiteNumber(value.returnedOrders) &&
  finiteNumber(value.returnedQuantity) &&
  finiteNumber(value.fulfilledOrders) &&
  optionalNumber(value.rate) &&
  moneyTotals(value.values) &&
  moneyTotals(value.labelCosts);
const monthRow = (value: any) =>
  reportMetrics(value) &&
  typeof value.month === 'string' &&
  typeof value.startDate === 'string' &&
  typeof value.endDate === 'string' &&
  optionalNumber(value.previousRate) &&
  optionalNumber(value.rateChange);
const productLineRow = (value: any) =>
  reportMetrics(value) && typeof value.productLine === 'string';
const skuReasonRow = (value: any) =>
  value &&
  ['sku', 'product', 'productLine', 'reason'].every(
    (key) => typeof value[key] === 'string',
  ) &&
  finiteNumber(value.returnCount) &&
  finiteNumber(value.returnedQuantity) &&
  moneyTotals(value.values) &&
  moneyTotals(value.labelCosts);
const detailRow = (value: any) =>
  value &&
  [
    'returnId',
    'returnItemId',
    'returnCreatedAt',
    'orderId',
    'sku',
    'product',
    'productLine',
    'reason',
    'resolution',
  ].every((key) => typeof value[key] === 'string') &&
  ['returnDeliveredAt', 'orderNumber', 'requestedResolution'].every((key) =>
    nullableText(value[key]),
  ) &&
  finiteNumber(value.quantity) &&
  optionalNumber(value.value) &&
  nullableText(value.currency) &&
  optionalNumber(value.labelCost) &&
  nullableText(value.labelCurrency);

const hasReportShape = (value: unknown): value is ReturnAnalyticsReport => {
  const report = value as ReturnAnalyticsReport | null;
  return Boolean(
    report &&
      typeof report.storeId === 'string' &&
      typeof report.startDate === 'string' &&
      typeof report.endDate === 'string' &&
      (report.dateBasis === 'CREATED' || report.dateBasis === 'DELIVERED') &&
      report.filters &&
      ['sku', 'productLine', 'reason', 'resolution'].every((key) =>
        nullableText((report.filters as any)[key]),
      ) &&
      typeof report.generatedAt === 'string' &&
      typeof report.complete === 'boolean' &&
      typeof report.denominatorDefinition === 'string' &&
      typeof report.productLineDefinition === 'string' &&
      reportMetrics(report.summary) &&
      typeof report.comparison?.startDate === 'string' &&
      typeof report.comparison?.endDate === 'string' &&
      reportMetrics(report.comparison?.summary) &&
      ['READY', 'PARTIAL', 'UNAVAILABLE'].includes(report.catalog?.status) &&
      finiteNumber(report.catalog?.matched) &&
      finiteNumber(report.catalog?.total) &&
      Array.isArray(report.warnings) &&
      report.warnings.every((warning) => typeof warning === 'string') &&
      Array.isArray(report.monthly) &&
      report.monthly.every(monthRow) &&
      Array.isArray(report.productLines) &&
      report.productLines.every(productLineRow) &&
      Array.isArray(report.skuReasons) &&
      report.skuReasons.every(skuReasonRow) &&
      Array.isArray(report.details) &&
      report.details.every(detailRow) &&
      ['productLines', 'reasons', 'resolutions'].every(
        (key) =>
          Array.isArray((report.options as any)?.[key]) &&
          (report.options as any)[key].every(
            (option: unknown) => typeof option === 'string',
          ),
      ),
  );
};

const approvedInputError = (error: any) => {
  const match = error?.graphQLErrors?.find(
    (item: any) =>
      item?.extensions?.code === 'BAD_USER_INPUT' &&
      typeof item.message === 'string' &&
      item.message.trim(),
  );
  return match?.message as string | undefined;
};

const sameFilters = (left: FilterValues, right: FilterValues) =>
  (['sku', 'productLine', 'reason', 'resolution'] as const).every(
    (key) => (left[key] || null) === (right[key] || null),
  );

const resultLabel = (count: number) =>
  `${count.toLocaleString()} ${count === 1 ? 'result' : 'results'}`;

export function ReturnsAnalyticsPanel({
  storeId,
  startDate,
  endDate,
  onLoadingChange,
}: ReturnsAnalyticsPanelProps) {
  const [params, setParams] = useSearchParams();
  const dateBasis =
    params.get('rrBasis') === 'DELIVERED' ? 'DELIVERED' : 'CREATED';
  const requestedBreakdown = params.get('rrBreakdown') as ReturnReportBreakdown;
  const breakdown = validBreakdowns.has(requestedBreakdown)
    ? requestedBreakdown
    : 'SKU_REASON';
  const appliedFilters = useMemo(() => readFilters(params), [params]);
  const appliedSku = appliedFilters.sku;
  const appliedProductLine = appliedFilters.productLine;
  const appliedReason = appliedFilters.reason;
  const appliedResolution = appliedFilters.resolution;
  const [draftFilters, setDraftFilters] =
    useState<FilterValues>(appliedFilters);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setDraftFilters({
      sku: appliedSku,
      productLine: appliedProductLine,
      reason: appliedReason,
      resolution: appliedResolution,
    });
  }, [
    storeId,
    appliedSku,
    appliedProductLine,
    appliedReason,
    appliedResolution,
  ]);

  const input: ReturnAnalyticsInput = {
    storeId,
    startDate,
    endDate,
    dateBasis,
    sku: appliedFilters.sku,
    productLine: appliedFilters.productLine,
    reason: appliedFilters.reason,
    resolution: appliedFilters.resolution,
  };
  const query = useQuery<ReturnAnalyticsQueryResult>(
    GET_RETURN_ANALYTICS_REPORT,
    {
      variables: { input },
      skip: !storeId,
      context: getMerchantRequestsContext(),
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );
  useEffect(() => {
    onLoadingChange?.(query.loading);
  }, [onLoadingChange, query.loading]);
  const candidate = query.data?.returnAnalytics;
  const shapedReport = hasReportShape(candidate) ? candidate : null;
  const reportMatches = Boolean(
    shapedReport &&
      shapedReport.storeId === storeId &&
      shapedReport.startDate === startDate &&
      shapedReport.endDate === endDate &&
      shapedReport.dateBasis === dateBasis &&
      sameFilters(shapedReport.filters, appliedFilters),
  );
  const report = reportMatches ? shapedReport : null;
  const incompleteMessage =
    shapedReport && reportMatches && !shapedReport.complete
      ? shapedReport.warnings[0] ||
        'Return & exchange analytics are incomplete. Narrow the date range and try again.'
      : null;
  const verificationProblem =
    !query.loading &&
    !query.error &&
    candidate &&
    (!shapedReport || !reportMatches)
      ? "Return & exchange analytics couldn't be verified for the selected filters."
      : null;
  const requestProblem =
    !query.loading && query.error
      ? approvedInputError(query.error) ||
        "Return & exchange analytics couldn't be loaded."
      : !query.loading && !candidate
      ? "Return & exchange analytics couldn't be loaded."
      : null;
  const analyticsReady = Boolean(
    !query.loading && report && report.complete && !requestProblem,
  );
  const productLineUnavailable = Boolean(
    report?.catalog.status === 'UNAVAILABLE',
  );
  const activeProductLineUnavailable =
    productLineUnavailable && breakdown === 'PRODUCT_LINE';
  const table = useMemo(
    () => (report ? buildReturnReportTable(report, breakdown) : null),
    [report, breakdown],
  );
  const totalRows = table?.rows.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleRows = table?.rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [breakdown, storeId, startDate, endDate, candidate?.generatedAt]);

  const updateSearch = (
    values: Record<string, string | null>,
    defaults: Record<string, string> = {},
  ) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => {
      if (!value || value === defaults[key]) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    updateSearch({
      rrSku: draftFilters.sku?.trim() || null,
      rrLine: draftFilters.productLine || null,
      rrReason: draftFilters.reason || null,
      rrResolution: draftFilters.resolution || null,
    });
    setPage(1);
  };

  const exportCsv = () => {
    if (!analyticsReady || activeProductLineUnavailable || !table || !totalRows)
      return;
    const csv = buildReturnReportCsv(table);
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    );
    const link = document.createElement('a');
    const inclusiveEnd = new Date(new Date(endDate).valueOf() - 1);
    const endLabel = Number.isNaN(inclusiveEnd.valueOf())
      ? endDate.slice(0, 10)
      : inclusiveEnd.toISOString().slice(0, 10);
    link.href = url;
    link.download = `shipaid-returns-exchanges-${table.fileLabel}-${startDate.slice(
      0,
      10,
    )}-to-${endLabel}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const exportLabel = `Export ${
    table?.title ||
    breakdowns.find((item) => item.value === breakdown)?.label ||
    'Return & exchange analytics'
  } CSV`;

  return (
    <section
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      aria-label="Return and exchange analytics"
    >
      <header className="flex flex-wrap items-start justify-between gap-4 px-5 py-5 sm:px-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
            Returns & Exchanges
          </p>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">
            Return & exchange analytics
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Compare return and exchange requests by original product, reason,
            and requested outcome.
          </p>
        </div>
        <button
          type="button"
          aria-label={exportLabel}
          disabled={
            !analyticsReady || activeProductLineUnavailable || !totalRows
          }
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <DownloadIcon className="h-4 w-4" aria-hidden="true" />
          <span>Export CSV</span>
        </button>
      </header>

      {analyticsReady && report ? (
        <section
          aria-label="Return and exchange activity summary"
          className="grid grid-cols-1 gap-3 border-t border-gray-100 px-5 py-5 sm:grid-cols-2 sm:px-6 lg:grid-cols-4"
        >
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs font-medium text-blue-700">
              Request activity ratio
            </p>
            <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
              {formatReportRate(report.summary.rate)}
            </p>
            <p className="mt-1 text-xs text-gray-600">
              {report.summary.returnedOrders.toLocaleString()} orders with
              requests ÷{' '}
              {report.summary.fulfilledOrders.toLocaleString()} fulfilled orders
            </p>
            <p className="mt-2 text-xs text-gray-600">
              May exceed 100% because requests can be for orders fulfilled in
              earlier periods.
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Previous period</p>
            <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
              {formatReportRate(report.comparison.summary.rate)}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Previous equal-length UTC period
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-gray-500">
              Requested quantity
            </p>
            <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
              {report.summary.returnedQuantity.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-gray-500">
              Original item value
            </p>
            <p className="mt-2 text-xl font-semibold tabular-nums text-gray-900">
              {formatMoneyTotals(report.summary.values)}
            </p>
          </div>
        </section>
      ) : null}

      <div className="border-y border-gray-200 bg-gray-50 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <label className="w-full text-sm font-medium text-gray-700 lg:w-56">
            Request date
            <select
              aria-label="Request date"
              value={dateBasis}
              onChange={(event) =>
                updateSearch(
                  { rrBasis: event.target.value },
                  { rrBasis: 'CREATED' },
                )
              }
              className={fieldClass}
            >
              <option value="CREATED">Created</option>
              <option value="DELIVERED">Delivered to warehouse</option>
            </select>
          </label>
          <fieldset className="min-w-0 flex-1">
            <legend className="text-sm font-medium text-gray-700">
              Breakdown
            </legend>
            <div
              role="group"
              aria-label="Report breakdown"
              className="mt-1 grid grid-cols-2 gap-1 rounded-lg bg-gray-200 p-1 sm:inline-grid sm:grid-cols-5"
            >
              {breakdowns.map((item) => {
                const selected = item.value === breakdown;
                const disabled =
                  item.value === 'PRODUCT_LINE' && productLineUnavailable;
                return (
                  <button
                    key={item.value}
                    type="button"
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => {
                      updateSearch(
                        { rrBreakdown: item.value },
                        { rrBreakdown: 'SKU_REASON' },
                      );
                      setPage(1);
                    }}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
                      selected
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-gray-600 hover:bg-white hover:text-gray-900'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        </div>

        <form
          onSubmit={applyFilters}
          className="mt-5 border-t border-gray-200 pt-5"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="text-sm font-medium text-gray-700">
              SKU
              <input
                aria-label="SKU"
                value={draftFilters.sku || ''}
                onChange={({ target: { value } }) =>
                  setDraftFilters((current) => ({
                    ...current,
                    sku: value,
                  }))
                }
                placeholder="All SKUs"
                className={fieldClass}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Product line
              <select
                aria-label="Product line"
                value={draftFilters.productLine || ''}
                disabled={productLineUnavailable}
                onChange={({ target: { value } }) =>
                  setDraftFilters((current) => ({
                    ...current,
                    productLine: value || null,
                  }))
                }
                className={fieldClass}
              >
                <option value="">All product lines</option>
                {(report?.options.productLines || []).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Reason
              <select
                aria-label="Reason"
                value={draftFilters.reason || ''}
                onChange={({ target: { value } }) =>
                  setDraftFilters((current) => ({
                    ...current,
                    reason: value || null,
                  }))
                }
                className={fieldClass}
              >
                <option value="">All reasons</option>
                {(report?.options.reasons || []).map((value) => (
                  <option key={value} value={value}>
                    {displayLabel(value)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Recorded resolution
              <select
                aria-label="Recorded resolution"
                value={draftFilters.resolution || ''}
                onChange={({ target: { value } }) =>
                  setDraftFilters((current) => ({
                    ...current,
                    resolution: value || null,
                  }))
                }
                className={fieldClass}
              >
                <option value="">All recorded resolutions</option>
                {(report?.options.resolutions || []).map((value) => (
                  <option key={value} value={value}>
                    {displayLabel(value)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-white hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => {
                setDraftFilters(emptyFilters);
                updateSearch({
                  rrSku: null,
                  rrLine: null,
                  rrReason: null,
                  rrResolution: null,
                });
              }}
            >
              Clear filters
            </button>
            <button
              type="submit"
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Apply filters
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-5 px-5 py-5 sm:px-6">
        {query.loading ? (
          <p role="status" className="text-sm text-gray-500">
            Loading return & exchange analytics…
          </p>
        ) : requestProblem || verificationProblem || incompleteMessage ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            <p>{requestProblem || verificationProblem || incompleteMessage}</p>
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="mt-2 font-medium underline"
            >
              Retry return & exchange analytics
            </button>
          </div>
        ) : report ? (
          <>
            <details className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              <summary className="cursor-pointer font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                Report definitions
              </summary>
              <div className="mt-3 space-y-1 text-xs leading-5 text-gray-500">
                <p>{report.denominatorDefinition}</p>
                <p>
                  This is an order activity ratio, not a fulfillment cohort. It
                  may exceed 100%.
                </p>
                <p>Product line: {report.productLineDefinition}.</p>
                <p>
                  Includes return and exchange requests. Denied and expired
                  requests are excluded.
                </p>
                <p>
                  Item quantities and values refer to original purchased items,
                  including items the customer may keep. They are not refund,
                  store credit, or replacement totals.
                </p>
                <p>
                  Recorded resolution reflects available settlement records;
                  it does not verify replacement completion.
                </p>
              </div>
            </details>

            {report.warnings.length > 0 && (
              <div
                role="alert"
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
              >
                <p className="font-medium">Some report data is unavailable.</p>
                {report.warnings.map((warning) => (
                  <p key={warning} className="mt-1 text-amber-800">
                    {warning}
                  </p>
                ))}
              </div>
            )}

            {activeProductLineUnavailable ? (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              >
                Product line analytics are unavailable.
              </p>
            ) : table && totalRows ? (
              <>
                {breakdown === 'REQUESTED_OUTCOME' && (
                  <p className="text-xs leading-5 text-gray-500">
                    Requested outcomes show customer intent, not completed
                    refunds, credits, or replacements. A request with different
                    item outcomes appears in each applicable category.
                  </p>
                )}
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      {table.title}
                    </h4>
                    <p className="mt-1 text-xs text-gray-500">
                      {resultLabel(totalRows)}
                    </p>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() =>
                          setPage((value) => Math.max(1, value - 1))
                        }
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-xs tabular-nums text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() =>
                          setPage((value) => Math.min(totalPages, value + 1))
                        }
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                  <table
                    aria-label={table.title}
                    className="min-w-full divide-y divide-gray-200"
                  >
                    <thead className="bg-gray-50">
                      <tr>
                        {table.columns.map((column) => {
                          const numeric = numericColumns.has(column);
                          return (
                            <th
                              key={column}
                              scope="col"
                              className={`whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 ${
                                numeric
                                  ? 'text-right tabular-nums'
                                  : 'text-left'
                              }`}
                            >
                              {column}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibleRows?.map((row, rowIndex) => (
                        <tr
                          key={`${currentPage}-${rowIndex}`}
                          className="transition-colors hover:bg-blue-50"
                        >
                          {row.map((value, columnIndex) => {
                            const numeric = numericColumns.has(
                              table.columns[columnIndex],
                            );
                            return (
                              <td
                                key={columnIndex}
                                className={`whitespace-nowrap px-4 py-3 text-sm text-gray-700 ${
                                  numeric
                                    ? 'text-right tabular-nums'
                                    : 'text-left'
                                }`}
                              >
                                {value}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">
                No {table?.title || 'return or exchange requests'} match these filters.
              </p>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}
